import React, { useState } from 'react';
import {
  Title,
  Text,
  Group,
  Stack,
  Card,
  Button,
  TextInput,
  Select,
  Badge,
  ActionIcon,
  Tooltip,
  Divider,
  SimpleGrid,
  Loader,
} from '@mantine/core';
import { PageContainer } from '../../components/ui/PageContainer';
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconEye,
  IconBuilding,
  IconUsers,
} from '@tabler/icons-react';
import { CreateTeamModal } from '../../components/team/CreateTeamModal';
import { modals } from '@mantine/modals';
import { EditTeamModal } from '../../components/team/EditTeamModal';
import { TeamViewModal } from '../../components/team/TeamViewModal';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { getErrorMessage } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { useTranslation } from 'react-i18next';
import { searchInTeam } from '../../utils/search';
import { useDevice } from '../../hooks/useDevice';
import { getActiveStatusColor } from '../../utils/statusUtils';
import { Team, CreateTeamData, User, Department } from '../../types';
import { AvatarsList } from '../../components/ui/AvatarsList';

export const Teams: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deviceType = useDevice();
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [teamForEdit, setTeamForEdit] = useState<Team | undefined>(undefined);
  const queryClient = useQueryClient();
  // Получение команд с бэкенда
  const { data: backendTeams = [], isLoading: isTeamsLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => api('/teams'),
    staleTime: 5 * 60 * 1000,
  });

  // Получение департаментов для фильтра
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => api('/departments'),
    staleTime: 5 * 60 * 1000,
  });

  // Используем данные с бэкенда как есть
  const teams: Team[] = backendTeams;

  const filteredTeams = teams.filter((team) => {
    const matchesSearch = searchInTeam(team, searchQuery);
    const matchesDepartment =
      departmentFilter === 'all' || team.department?._id === departmentFilter;

    return matchesSearch && matchesDepartment;
  });

  const handleViewTeam = (team: Team) => {
    // Получаем полные данные команды с бэкенда для просмотра
    const fetchTeamForView = async () => {
      try {
        const fullTeam = await api(`/teams/${team._id}`);
        setSelectedTeam(fullTeam);
        setShowTeamModal(true);
      } catch (error) {
        console.error('Error fetching team for view:', error);
        // Если не удалось получить полные данные, используем базовые
        setSelectedTeam(team);
        setShowTeamModal(true);
      }
    };

    fetchTeamForView();
  };

  const handleEditTeam = (team: Team) => {
    // Нужно получить полные данные команды из бэкенда для редактирования
    // потому что нам нужны объекты User, а не только ID
    const fetchTeamForEdit = async () => {
      try {
        const fullTeam = await api(`/teams/${team._id}`);

        setTeamForEdit(fullTeam);
        setEditModalOpened(true);
      } catch (error) {
        showToast({ title: t('common.error'), message: t('teams.loadError'), color: 'red' });
      }
    };

    fetchTeamForEdit();
  };

  // Мутация удаления команды
  const deleteTeamMutation = useMutation({
    mutationFn: async (teamId: string) => {
      return api(`/teams/${teamId}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      showToast({ title: t('teams.deleted'), message: t('teams.deletedMsg'), color: 'teal' });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('teams.deleteError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const handleDeleteTeam = (team: Team) => {
    modals.openConfirmModal({
      title: t('teams.deleteConfirmTitle', { name: team.name }),
      centered: true,
      children: (
        <Text
          size="sm"
          dangerouslySetInnerHTML={{
            __html: t('teams.deleteConfirmText', { name: team.name }),
          }}
        />
      ),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        deleteTeamMutation.mutate(team._id);
      },
    });
  };

  // Мутация создания команды
  const createTeamMutation = useMutation({
    mutationFn: async (teamData: CreateTeamData) => {
      return api('/teams', {
        method: 'POST',
        body: JSON.stringify(teamData),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },
    onSuccess: () => {
      showToast({ title: t('teams.created'), message: t('teams.createdMsg'), color: 'teal' });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setShowCreateModal(false);
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('teams.createError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const handleCreateTeam = (teamData: CreateTeamData) => {
    createTeamMutation.mutate(teamData);
  };

  // Вспомогательные функции для работы с командами
  const getTeamStats = (team: Team) => {
    const totalMembers = (team.members?.length || 0) + (team.lead ? 1 : 0);
    let activeMembers = team.members?.filter((member) => member.user?.active !== false).length || 0;
    if (team.lead && team.lead.active !== false) {
      activeMembers += 1;
    }
    return { totalMembers, activeMembers };
  };

  const getStatusColor = getActiveStatusColor;

  return (
    <PageContainer>
      {/* Заголовок */}
      <Group gap="md" align="center" mb={{ base: 'md', sm: 'xl' }}>
        <Title order={2}>{t('teams.title')}</Title>
      </Group>

      <Stack gap="lg">
        {/* Фильтры и поиск */}
        <Card shadow="sm" padding={deviceType === 'mobile' ? 'md' : 'xl'} radius="md" withBorder>
          <Stack gap={deviceType === 'mobile' ? 'md' : 'lg'}>
            <Group
              justify="space-between"
              align="end"
              wrap="wrap"
              gap={deviceType === 'mobile' ? 'sm' : 'md'}
            >
              <Group
                gap={deviceType === 'mobile' ? 'sm' : 'md'}
                style={{ flex: 1, minWidth: 0 }}
                w={deviceType === 'mobile' ? '100%' : 'auto'}
              >
                <TextInput
                  placeholder={t('teams.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftSection={<IconSearch size={16} />}
                  style={{ flex: 1 }}
                  w={deviceType === 'mobile' ? '100%' : 'auto'}
                />
                <Select
                  placeholder={t('teams.department')}
                  value={departmentFilter}
                  onChange={(value) => setDepartmentFilter(value || 'all')}
                  data={[
                    { value: 'all', label: t('teams.allDepartments') },
                    ...departments.map((dept: Department) => ({
                      value: dept._id,
                      label: dept.name,
                    })),
                  ]}
                  w={deviceType === 'mobile' ? '100%' : 200}
                />
              </Group>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => setShowCreateModal(true)}
                variant="light"
                w={deviceType === 'mobile' ? '100%' : 'auto'}
              >
                {t('teams.create')}
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Карточки команд */}
        {isTeamsLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : teams.length === 0 ? (
          <Stack align="center" gap="md" py="xl">
            <IconUsers size={48} style={{ color: 'var(--mantine-color-gray-4)' }} />
            <div>
              <Text size="lg" fw={500} ta="center">
                {t('teams.empty')}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                {t('teams.emptyDescription')}
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setShowCreateModal(true)}>
              {t('teams.createFirst')}
            </Button>
          </Stack>
        ) : filteredTeams.length === 0 ? (
          <Stack align="center" gap="md" py="xl">
            <IconSearch size={48} style={{ color: 'var(--mantine-color-gray-4)' }} />
            <div>
              <Text size="lg" fw={500} ta="center">
                {t('teams.noResults')}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                {t('teams.noResultsDescription')}
              </Text>
            </div>
            <Button
              variant="light"
              onClick={() => {
                setSearchQuery('');
                setDepartmentFilter('all');
              }}
            >
              {t('teams.clearFilters')}
            </Button>
          </Stack>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {filteredTeams.map((team) => (
              <Card
                key={team._id}
                shadow="sm"
                padding={deviceType === 'mobile' ? 'md' : 'xl'}
                radius="md"
                withBorder
              >
                <Stack gap={deviceType === 'mobile' ? 'sm' : 'md'}>
                  {/* Заголовок команды */}
                  <Group justify="space-between" align="start">
                    <div>
                      <Title order={3} mb="xs">
                        {team.name}
                      </Title>
                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {team.description}
                      </Text>
                    </div>
                    <Badge color={getStatusColor(team.status || 'active')} variant="light">
                      {team.status === 'active' ? t('teams.active') : t('teams.inactive')}
                    </Badge>
                  </Group>

                  <Divider />

                  {/* Статистика команды */}
                  <Stack gap="xs">
                    <Group justify="space-between" align="center">
                      <Text size="sm" fw={500}>
                        {t('teams.members')}
                      </Text>
                      <AvatarsList
                        items={[team.lead, ...(team.members || []).map((m) => m.user)].filter(
                          (user): user is User => user != null,
                        )}
                        onItemClick={(userId) => navigate(`/profile/${userId}`)}
                      />
                    </Group>

                    <Group justify="space-between">
                      <Text size="sm" fw={500}>
                        {t('teams.activeMembers')}
                      </Text>
                      <Text size="sm">
                        {t('teams.activeOfTotal', {
                          active: getTeamStats(team).activeMembers,
                          total: getTeamStats(team).totalMembers,
                        })}
                      </Text>
                    </Group>
                  </Stack>

                  <Divider />

                  {/* Информация о команде */}
                  <Stack gap="xs">
                    <Group gap="xs">
                      <IconBuilding size={14} />
                      <Text size="sm">{team.department?.name || 'N/A'}</Text>
                    </Group>
                  </Stack>

                  {/* Действия */}
                  <Group gap="xs" justify="flex-end">
                    <Tooltip label={t('teams.view')}>
                      <ActionIcon variant="light" onClick={() => handleViewTeam(team)} color="blue">
                        <IconEye size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t('common.edit')}>
                      <ActionIcon
                        variant="light"
                        onClick={() => handleEditTeam(team)}
                        color="teal"
                        aria-label={t('common.edit')}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t('teams.delete')}>
                      <ActionIcon
                        variant="light"
                        onClick={() => handleDeleteTeam(team)}
                        color="red"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>

      {/* Модальное окно просмотра команды */}
      <TeamViewModal
        opened={showTeamModal}
        onClose={() => setShowTeamModal(false)}
        onEdit={() => {
          if (selectedTeam) {
            handleEditTeam(selectedTeam);
            setShowTeamModal(false);
          }
        }}
        team={selectedTeam}
      />

      {/* Модальное окно создания команды */}
      <CreateTeamModal
        opened={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateTeam}
        loading={createTeamMutation.isPending}
      />

      <EditTeamModal
        opened={editModalOpened}
        onClose={() => {
          setEditModalOpened(false);
          setTeamForEdit(undefined);
        }}
        onSubmit={() => {
          setEditModalOpened(false);
          setTeamForEdit(undefined);
        }}
        team={teamForEdit}
        loading={false}
      />
    </PageContainer>
  );
};
